import { Link } from 'react-router-dom';
import competitionsData from '@/data/competitions.json';
import type { CompetitionEvent } from '@/data/types';

const events = competitionsData as CompetitionEvent[];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

const MEDALS = ['🥇', '🥈', '🥉'];

export default function Competitions() {
  if (events.length === 0) return null;

  // Most recent event (events without a date sort last).
  const event = [...events].sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
  const date = formatDate(event.date);
  const teamCount = event.categories.reduce((n, c) => n + c.results.length, 0);

  // Overall top 3 finishers across all categories — a podium teaser.
  const podium = event.categories
    .flatMap((c) => c.results)
    .filter((r) => r.timeSeconds !== null)
    .sort((a, b) => (a.timeSeconds as number) - (b.timeSeconds as number))
    .slice(0, 3);

  return (
    <section id="events" className="py-16 lg:py-32 px-4 lg:px-8 bg-[#0a0a0a] border-t border-white/5">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-14">
          <span data-animate className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
            <span className="w-4 h-px bg-[#14B8A6]" />
            COMPETITIONS
            <span className="w-4 h-px bg-[#14B8A6]" />
          </span>
          <h2 data-animate data-delay="1" className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 text-white">
            Competitions &amp; Results
          </h2>
          <p data-animate data-delay="2" className="text-base lg:text-xl text-gray-400 max-w-2xl mx-auto">
            We host events that push our community. Browse full leaderboards and find your name.
          </p>
        </div>

        {/* Clickable overview card → /events */}
        <Link
          to="/events"
          data-animate
          data-delay="2"
          className="group block bg-[#1a1a1a] rounded-3xl border border-white/[0.08] hover:border-[#14B8A6]/30 hover:shadow-[0_0_50px_rgba(20,184,166,0.12)] transition-all duration-300 overflow-hidden"
        >
          <div className="p-6 lg:p-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Event info */}
              <div>
                <span className="inline-block text-xs font-semibold text-[#14B8A6] bg-[#14B8A6]/10 border border-[#14B8A6]/20 px-3 py-1 rounded-full mb-4">
                  {event.discipline}
                </span>
                <h3 className="text-2xl lg:text-4xl font-bold text-white mb-3">{event.name}</h3>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-gray-400 text-sm">
                  {date && (
                    <span className="inline-flex items-center gap-2">
                      <i className="ri-calendar-line text-[#14B8A6]" />
                      {date}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-team-line text-[#14B8A6]" />
                    {teamCount} teams
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-list-ordered text-[#14B8A6]" />
                    {event.categories.length} categories
                  </span>
                </div>
              </div>

              {/* CTA */}
              <span className="inline-flex items-center justify-center gap-2 bg-[#14B8A6] group-hover:bg-[#0F9A8A] text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-300 flex-shrink-0 self-start lg:self-center">
                View full results
                <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>

            {/* Podium preview */}
            {podium.length === 3 && (
              <div className="mt-8 pt-8 border-t border-white/[0.06]">
                <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-4">
                  Overall podium
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {podium.map((r, i) => (
                    <div
                      key={r.bib}
                      className="bg-white/[0.03] border border-white/[0.06] rounded-2xl px-4 py-3 flex items-center gap-3"
                    >
                      <span className="text-2xl flex-shrink-0">{MEDALS[i]}</span>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium leading-snug">
                          {r.athletes[0]} <span className="text-gray-600">&amp;</span>
                          <br />
                          {r.athletes[1]}
                        </p>
                        <p className="text-[#14B8A6] text-xs font-mono mt-1">
                          {formatTime(r.timeSeconds as number)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Link>
      </div>
    </section>
  );
}
